import { CalendarDays, Car, ClipboardList } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { useContent } from "@/context/ContentContext";

function sortByOrder(items) {
  return [...items].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
}

export default function Gallery() {
  const c = useContent();
  const items = sortByOrder(c.gallery || []).filter((i) => i.visible !== false);
  if (!items.length) return null;

  return (
    <section id="referenzen" data-testid="gallery-section" className="relative py-24 sm:py-32 bg-[#0C0C0E] border-y border-white/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead
          eyebrow={c.texts.gallery_eyebrow}
          title={c.texts.gallery_title}
          sub={c.texts.gallery_sub}
        />
        <Reveal delay={0.08}>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-wider text-blue-400">
            Eigene Arbeitsproben – keine Stockfotos
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((item, i) => (
            <Reveal key={item.id} delay={(i % 2) * 0.08}>
              <figure
                data-testid="gallery-item"
                className="overflow-hidden rounded-2xl border border-[#27272A] bg-[#121215]"
              >
                {item.title ? (
                  <figcaption className="px-5 pt-4 pb-3">
                    <p className="font-display text-base font-semibold text-zinc-100">{item.title}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                      {item.vehicle ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-zinc-400">
                          <Car className="h-3.5 w-3.5 text-blue-400" /> {item.vehicle}
                        </span>
                      ) : null}
                      {item.service ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-zinc-400">
                          <ClipboardList className="h-3.5 w-3.5 text-blue-400" /> {item.service}
                        </span>
                      ) : null}
                      {item.date ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-zinc-400">
                          <CalendarDays className="h-3.5 w-3.5 text-blue-400" /> {item.date}
                        </span>
                      ) : null}
                    </div>
                  </figcaption>
                ) : null}
                <div className="grid grid-cols-2 gap-px bg-[#27272A]">
                  <div className="relative">
                    <img
                      src={item.before_url}
                      alt={`Vorher: ${item.title || item.vehicle || "Fahrzeuginnenraum"} vor der Reinigung`}
                      loading="lazy"
                      decoding="async"
                      className="h-56 sm:h-72 w-full object-cover"
                    />
                    <span className="absolute top-3 left-3 rounded-full bg-zinc-950/85 border border-zinc-600 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-zinc-200">
                      Vorher
                    </span>
                  </div>
                  <div className="relative">
                    <img
                      src={item.after_url}
                      alt={`Nachher: ${item.title || item.vehicle || "Fahrzeuginnenraum"} nach der Reinigung`}
                      loading="lazy"
                      decoding="async"
                      className="h-56 sm:h-72 w-full object-cover"
                    />
                    <span className="absolute top-3 left-3 rounded-full bg-blue-600 border border-blue-500 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-white">
                      Nachher
                    </span>
                  </div>
                </div>
                {item.desc ? (
                  <figcaption className="px-5 py-4 text-sm text-zinc-300 leading-relaxed">{item.desc}</figcaption>
                ) : null}
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
